import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Database, RefreshCw, Zap, Settings, Search, 
  TrendingUp, Clock, CheckCircle, AlertCircle, 
  Play, Pause, BarChart3, Download, Upload,
  Loader2, ChevronRight, Filter, X, Image as ImageIcon, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminDataPipeline = () => {
  const [status, setStatus] = useState(null);
  const [qualityReport, setQualityReport] = useState(null);
  const [enrichmentQueue, setEnrichmentQueue] = useState([]);
  const [searchStats, setSearchStats] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enriching, setEnriching] = useState(false);
  
  // Manual enrichment form
  const [enrichBrand, setEnrichBrand] = useState('');
  const [enrichModel, setEnrichModel] = useState('');
  
  // Settings
  const [settings, setSettings] = useState({
    daily_limit: 50,
    cooldown_hours: 24,
    priority_threshold: 3
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const [tavilyBudget, setTavilyBudget] = useState(null);
  const [savingTavily, setSavingTavily] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobDetail, setJobDetail] = useState(null);
  const [cleaningImages, setCleaningImages] = useState(false);
  const [regeneratingImages, setRegeneratingImages] = useState(false);
  const [imageStats, setImageStats] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [statusRes, qualityRes, queueRes, statsRes, jobsRes, tavilyRes] = await Promise.all([
        axios.get(`${API}/api/data-pipeline/status`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/data-pipeline/quality-report`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/data-pipeline/enrichment-queue?limit=10`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/data-pipeline/search-stats?limit=20`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/data-pipeline/jobs?limit=20`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/data-pipeline/tavily-budget`, { headers: getAuthHeaders() }).catch(() => ({ data: null }))
      ]);
      
      setStatus(statusRes.data);
      setQualityReport(qualityRes.data);
      setEnrichmentQueue(queueRes.data.queue || []);
      setSearchStats(statsRes.data.stats || []);
      setJobs(jobsRes.data.jobs || []);
      setTavilyBudget(tavilyRes.data);
      
      // Update settings from status
      if (statusRes.data) {
        setSettings({
          daily_limit: statusRes.data.daily_limit,
          cooldown_hours: statusRes.data.cooldown_hours,
          priority_threshold: statusRes.data.priority_threshold
        });
      }
    } catch (error) {
      console.error('Error fetching pipeline data:', error);
      toast.error('Fehler beim Laden der Pipeline-Daten');
    } finally {
      setLoading(false);
    }
  }, []);

  const updateTavilyBudget = async (updates) => {
    setSavingTavily(true);
    try {
      const res = await axios.put(
        `${API}/api/data-pipeline/tavily-budget`,
        updates,
        { headers: getAuthHeaders() }
      );
      setTavilyBudget(res.data);
      toast.success('Tavily-Budget aktualisiert');
    } catch (err) {
      toast.error('Update fehlgeschlagen');
    } finally {
      setSavingTavily(false);
    }
  };

  // Load detailed enrichment data when a job is selected
  useEffect(() => {
    if (!selectedJob) {
      setJobDetail(null);
      return;
    }
    (async () => {
      try {
        // Get the enriched catalog entry for this brand+model
        const res = await axios.get(
          `${API}/api/data-pipeline/job-detail`,
          {
            headers: getAuthHeaders(),
            params: {
              brand: selectedJob.brand,
              model: selectedJob.model || '',
              job_id: selectedJob.id || selectedJob._id || '',
            }
          }
        );
        setJobDetail(res.data);
      } catch (err) {
        console.error('Job detail load failed', err);
        setJobDetail({ error: err.response?.data?.detail || 'Detail konnte nicht geladen werden' });
      }
    })();
  }, [selectedJob]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleManualEnrich = async () => {
    if (!enrichBrand.trim()) {
      toast.error('Bitte Marke eingeben');
      return;
    }
    
    setEnriching(true);
    try {
      const response = await axios.post(
        `${API}/api/data-pipeline/enrich`,
        { brand: enrichBrand, model: enrichModel || null, force: false },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        toast.success(`${enrichBrand} ${enrichModel || ''} wird angereichert`);
        setEnrichBrand('');
        setEnrichModel('');
        fetchData();
      } else {
        toast.error(response.data.error || 'Anreicherung fehlgeschlagen');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei Anreicherung');
    } finally {
      setEnriching(false);
    }
  };

  const handleAutoEnrich = async () => {
    setEnriching(true);
    try {
      const response = await axios.post(
        `${API}/api/data-pipeline/auto-enrich?max_items=5`,
        {},
        { headers: getAuthHeaders() }
      );

      const { success = 0, processed = 0, message, queue_source } = response.data || {};

      if (processed === 0) {
        toast.info(message || 'Keine Fahrzeuge zur Anreicherung verfügbar');
      } else {
        const sourceLabel = queue_source === 'incomplete_brands'
          ? ' (Marken ohne Modelle)'
          : queue_source === 'stale_brands'
          ? ' (veraltete Marken)'
          : queue_source === 'search_priority'
          ? ' (beliebte Suchen)'
          : '';
        toast.success(`${success}/${processed} erfolgreich angereichert${sourceLabel}`);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Auto-Anreicherung fehlgeschlagen');
    } finally {
      setEnriching(false);
    }
  };

  const handleResetCounter = async () => {
    try {
      await axios.delete(`${API}/api/data-pipeline/reset-daily-counter`, { headers: getAuthHeaders() });
      toast.success('Tageszähler zurückgesetzt');
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Zurücksetzen');
    }
  };

  const [seedingWikidata, setSeedingWikidata] = useState(false);
  const [maxBrandsToSeed, setMaxBrandsToSeed] = useState(50);

  const handleSeedWikidata = async () => {
    if (!window.confirm(`Wikidata-Seeding startet im Hintergrund (max ${maxBrandsToSeed} Marken). Fortfahren?`)) return;
    setSeedingWikidata(true);
    try {
      const res = await axios.post(
        `${API}/api/data-pipeline/seed-wikidata`,
        { max_brands: maxBrandsToSeed, include_models: true, only_missing: true },
        { headers: getAuthHeaders() }
      );
      if (res.data?.status === 'started') {
        toast.success(res.data.message || 'Wikidata-Seeding gestartet');
      } else if (res.data?.status === 'already_running') {
        toast.info('Wikidata-Seeding läuft bereits');
      }
      // Poll status briefly
      setTimeout(() => fetchData(), 5000);
    } catch (err) {
      toast.error('Seeding fehlgeschlagen: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSeedingWikidata(false);
    }
  };

  const handleCleanupImages = async () => {
    if (!window.confirm('Alle Bild-Verweise prüfen und fehlende Dateien als "Bild fehlt" markieren?')) return;
    setCleaningImages(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/vehicle-master/cleanup-missing-images`,
        {},
        { headers: getAuthHeaders() }
      );
      setImageStats(res.data);
      toast.success(`Geprüft: ${res.data.checked}, fehlend bereinigt: ${res.data.cleared}, valide: ${res.data.remaining_valid}`);
    } catch (err) {
      toast.error('Cleanup fehlgeschlagen');
    } finally {
      setCleaningImages(false);
    }
  };

  const handleRegenerateImages = async (limit = 10) => {
    if (!window.confirm(`${limit} fehlende Bilder neu generieren? (Verbraucht LLM-Budget)`)) return;
    setRegeneratingImages(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/vehicle-master/regenerate-missing-images?limit=${limit}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`${res.data.success}/${res.data.processed} Bilder neu generiert (${res.data.failed} fehlgeschlagen)`);
      // Re-run cleanup to update stats
      await handleCleanupImages();
    } catch (err) {
      toast.error('Regenerierung fehlgeschlagen');
    } finally {
      setRegeneratingImages(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.put(
        `${API}/api/data-pipeline/settings`,
        settings,
        { headers: getAuthHeaders() }
      );
      toast.success('Einstellungen gespeichert');
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleEnrichFromQueue = async (item) => {
    setEnriching(true);
    try {
      const response = await axios.post(
        `${API}/api/data-pipeline/enrich`,
        { brand: item.brand, model: item.model || null, force: false },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        toast.success(`${item.brand} ${item.model || ''} angereichert`);
        fetchData();
      } else {
        toast.error(response.data.error || 'Fehlgeschlagen');
      }
    } catch (error) {
      toast.error('Fehler bei Anreicherung');
    } finally {
      setEnriching(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen lg:ml-64 p-4 sm:p-8 flex items-center justify-center" style={{ backgroundColor: '#0B0F14' }}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#1EA0E6] mx-auto mb-4" />
          <p className="text-gray-400">Lade Data Pipeline...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen lg:ml-64 p-4 sm:p-8" style={{ backgroundColor: '#0B0F14' }}>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
              <Database className="w-8 h-8 text-[#1EA0E6]" />
              Data Pipeline
            </h1>
            <p className="text-gray-400 mt-1">Unified Vehicle Data Collection & Enrichment</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={fetchData}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Aktualisieren
            </Button>
          </div>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Daily Budget */}
          <Card className="bg-[#0F141A] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Tagesbudget</p>
                  <p className="text-2xl font-bold text-white">
                    {status?.used_today || 0} / {status?.daily_limit || 50}
                  </p>
                </div>
                <div className={`p-3 rounded-full ${status?.remaining_today > 10 ? 'bg-green-500/20' : 'bg-yellow-500/20'}`}>
                  <Zap className={`w-6 h-6 ${status?.remaining_today > 10 ? 'text-green-400' : 'text-yellow-400'}`} />
                </div>
              </div>
              <div className="mt-3 h-2 bg-white/10 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-[#1EA0E6] to-[#22C55E]"
                  style={{ width: `${((status?.used_today || 0) / (status?.daily_limit || 50)) * 100}%` }}
                />
              </div>
            </CardContent>
          </Card>

          {/* Data Quality */}
          <Card className="bg-[#0F141A] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Datenqualität</p>
                  <p className="text-2xl font-bold text-white">
                    {qualityReport?.completeness?.percentage || 0}%
                  </p>
                </div>
                <div className="p-3 rounded-full bg-[#1EA0E6]/20">
                  <BarChart3 className="w-6 h-6 text-[#1EA0E6]" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {qualityReport?.totals?.brands || 0} Marken, {qualityReport?.totals?.models || 0} Modelle
              </p>
            </CardContent>
          </Card>

          {/* Pending Enrichments */}
          <Card className="bg-[#0F141A] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Warteschlange</p>
                  <p className="text-2xl font-bold text-white">
                    {qualityReport?.enrichment_stats?.pending_enrichments || 0}
                  </p>
                </div>
                <div className="p-3 rounded-full bg-purple-500/20">
                  <Clock className="w-6 h-6 text-purple-400" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">Beliebte Fahrzeuge warten</p>
            </CardContent>
          </Card>

          {/* Auto-Enrich Button */}
          <Card className="bg-[#0F141A] border-white/10">
            <CardContent className="p-4">
              <Button
                onClick={handleAutoEnrich}
                disabled={enriching || (status?.remaining_today || 0) <= 0}
                className="w-full h-full bg-gradient-to-r from-[#1EA0E6] to-[#22C55E] hover:opacity-90"
              >
                {enriching ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Play className="w-5 h-5 mr-2" />
                    Auto-Anreicherung
                  </>
                )}
              </Button>
              <p className="text-xs text-gray-500 mt-2 text-center">
                Nächste 5 priorisierte Fahrzeuge
              </p>
            </CardContent>
          </Card>
        </div>

        {/* SCHICHT 1: Wikidata Seeding - Strukturdaten */}
        <Card className="bg-gradient-to-br from-purple-900/20 to-cyan-900/20 border-purple-500/30">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Database className="w-5 h-5 text-purple-400" />
              Schicht 1: Wikidata Strukturdaten-Seed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-3 mb-4">
              <p className="text-purple-200 text-sm">
                <strong>Zuverlässige Basis-Daten:</strong> Lädt Hersteller + Modelle aus Wikidata (kostenlos, validiert, idempotent).
                Liefert die saubere Datenstruktur, auf die später Specs/Bilder/Tuning aufbauen.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <label className="text-xs text-gray-400 block mb-1">Max. Marken pro Lauf</label>
                <Input
                  type="number"
                  min="1"
                  max="500"
                  value={maxBrandsToSeed}
                  onChange={(e) => setMaxBrandsToSeed(parseInt(e.target.value) || 50)}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="wikidata-max-brands"
                />
                <p className="text-xs text-gray-500 mt-1">Tipp: Erststart mit 50, danach Rest in Batches</p>
              </div>
              <div className="flex items-end">
                <Button
                  onClick={handleSeedWikidata}
                  disabled={seedingWikidata}
                  className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:opacity-90 whitespace-nowrap"
                  data-testid="wikidata-seed-btn"
                >
                  {seedingWikidata ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Database className="w-4 h-4 mr-2" />
                  )}
                  Wikidata Seeding starten
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* BILD-MANAGEMENT */}
        <Card className="bg-gradient-to-br from-pink-900/15 to-orange-900/10 border-pink-500/30">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-pink-400" />
              Bild-Management
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-pink-500/10 border border-pink-500/20 rounded-lg p-3 mb-4 text-sm text-pink-200">
              Nach Container-Restarts können Bild-Dateien fehlen, obwohl die DB sie als "vorhanden" markiert.
              <br />
              <strong>Prüfen → fehlende bereinigen → neu generieren</strong> sorgt für saubere Daten.
            </div>

            {imageStats && (
              <div className="grid grid-cols-3 gap-2 text-center mb-4">
                <div className="bg-white/5 rounded p-2">
                  <p className="text-white text-xl font-bold">{imageStats.checked}</p>
                  <p className="text-[10px] text-gray-400">Geprüft</p>
                </div>
                <div className="bg-emerald-500/10 rounded p-2">
                  <p className="text-emerald-300 text-xl font-bold">{imageStats.remaining_valid}</p>
                  <p className="text-[10px] text-gray-400">Valide Bilder</p>
                </div>
                <div className="bg-red-500/10 rounded p-2">
                  <p className="text-red-300 text-xl font-bold">{imageStats.cleared}</p>
                  <p className="text-[10px] text-gray-400">Fehlend bereinigt</p>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                onClick={handleCleanupImages}
                disabled={cleaningImages}
                className="bg-pink-600 hover:bg-pink-700"
                data-testid="cleanup-images-btn"
              >
                {cleaningImages ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                Bild-Pfade prüfen
              </Button>
              <Button
                onClick={() => handleRegenerateImages(5)}
                disabled={regeneratingImages}
                variant="outline"
                className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                data-testid="regen-images-5-btn"
              >
                {regeneratingImages ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                5 Bilder neu generieren
              </Button>
              <Button
                onClick={() => handleRegenerateImages(20)}
                disabled={regeneratingImages}
                variant="outline"
                className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                data-testid="regen-images-20-btn"
              >
                {regeneratingImages ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                20 Bilder neu generieren
              </Button>
            </div>
          </CardContent>
        </Card>



        {/* TAVILY MONATSBUDGET */}
        {tavilyBudget && (
          <Card className="bg-gradient-to-br from-emerald-900/20 to-amber-900/10 border-emerald-500/30">            <CardHeader>
              <CardTitle className="text-white flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-emerald-400" />
                  Tavily Monatsbudget {tavilyBudget.period && <span className="text-xs text-gray-400">({tavilyBudget.period})</span>}
                </span>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  tavilyBudget.auto_run_enabled
                    ? 'bg-emerald-500/30 text-emerald-300'
                    : 'bg-gray-500/30 text-gray-400'
                }`}>
                  Auto: {tavilyBudget.auto_run_enabled ? 'AN' : 'AUS'}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Progress bar */}
              <div className="mb-4">
                <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
                  <span>{tavilyBudget.credits_used} / {tavilyBudget.monthly_budget} Credits</span>
                  <span>Auto-Stop bei {tavilyBudget.auto_stop_at} (75%)</span>
                </div>
                <div className="h-3 bg-white/5 rounded-full overflow-hidden relative">
                  {/* 75% threshold marker */}
                  <div
                    className="absolute top-0 bottom-0 w-px bg-amber-400/60 z-10"
                    style={{ left: `${(tavilyBudget.usage_threshold_pct || 0.75) * 100}%` }}
                    title="Auto-Stop bei 75%"
                  />
                  <div
                    className={`h-full transition-all duration-500 ${
                      tavilyBudget.usage_pct >= 75
                        ? 'bg-red-500'
                        : tavilyBudget.usage_pct >= 50
                        ? 'bg-amber-500'
                        : 'bg-emerald-500'
                    }`}
                    style={{ width: `${Math.min(100, tavilyBudget.usage_pct || 0)}%` }}
                  />
                </div>
                <div className="grid grid-cols-3 gap-2 mt-3 text-center text-xs">
                  <div className="bg-white/5 rounded p-2">
                    <p className="text-emerald-400 font-bold">{tavilyBudget.searches_advanced || 0}</p>
                    <p className="text-gray-500">Suchen (Advanced)</p>
                  </div>
                  <div className="bg-white/5 rounded p-2">
                    <p className="text-cyan-400 font-bold">{tavilyBudget.credits_remaining_to_threshold || 0}</p>
                    <p className="text-gray-500">Bis Auto-Stop</p>
                  </div>
                  <div className="bg-white/5 rounded p-2">
                    <p className="text-white font-bold">{tavilyBudget.credits_remaining_total || 0}</p>
                    <p className="text-gray-500">Bis Monatsende</p>
                  </div>
                </div>
              </div>

              {/* Configuration */}
              <div className="grid sm:grid-cols-4 gap-3 mt-4 pt-4 border-t border-white/10">
                <div>
                  <label className="text-xs text-gray-400 block mb-1">Monatsbudget</label>
                  <Input
                    type="number"
                    defaultValue={tavilyBudget.monthly_budget}
                    onBlur={(e) => {
                      const v = parseInt(e.target.value);
                      if (v && v !== tavilyBudget.monthly_budget) updateTavilyBudget({ monthly_budget: v });
                    }}
                    className="bg-white/5 border-white/10 text-white text-sm h-9"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-400 block mb-1">Stop-Schwelle %</label>
                  <Input
                    type="number"
                    step="5"
                    min="10"
                    max="100"
                    defaultValue={Math.round((tavilyBudget.usage_threshold_pct || 0.75) * 100)}
                    onBlur={(e) => {
                      const v = parseInt(e.target.value);
                      if (v) updateTavilyBudget({ usage_threshold_pct: v / 100 });
                    }}
                    className="bg-white/5 border-white/10 text-white text-sm h-9"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-400 block mb-1">Interval (h)</label>
                  <Input
                    type="number"
                    min="1"
                    max="48"
                    defaultValue={tavilyBudget.scheduler_interval_hours}
                    onBlur={(e) => {
                      const v = parseInt(e.target.value);
                      if (v && v !== tavilyBudget.scheduler_interval_hours) updateTavilyBudget({ scheduler_interval_hours: v });
                    }}
                    className="bg-white/5 border-white/10 text-white text-sm h-9"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-400 block mb-1">Max/Run</label>
                  <Input
                    type="number"
                    min="1"
                    max="50"
                    defaultValue={tavilyBudget.max_per_run}
                    onBlur={(e) => {
                      const v = parseInt(e.target.value);
                      if (v && v !== tavilyBudget.max_per_run) updateTavilyBudget({ max_per_run: v });
                    }}
                    className="bg-white/5 border-white/10 text-white text-sm h-9"
                  />
                </div>
              </div>

              <div className="flex gap-2 mt-4">
                <Button
                  onClick={() => updateTavilyBudget({ auto_run_enabled: !tavilyBudget.auto_run_enabled })}
                  disabled={savingTavily}
                  className={tavilyBudget.auto_run_enabled
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-gradient-to-r from-emerald-600 to-cyan-600 hover:opacity-90'
                  }
                  data-testid="tavily-toggle-auto-run"
                >
                  {savingTavily ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : tavilyBudget.auto_run_enabled ? (
                    <>
                      <X className="w-4 h-4 mr-2" /> Auto-Run deaktivieren
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-2" /> Auto-Run aktivieren
                    </>
                  )}
                </Button>
              </div>

              <p className="text-xs text-gray-500 mt-3">
                Auto-Run startet alle {tavilyBudget.scheduler_interval_hours}h eine Anreicherung von max {tavilyBudget.max_per_run} Marken
                und stoppt automatisch bei {Math.round((tavilyBudget.usage_threshold_pct || 0.75) * 100)}% Budget-Verbrauch.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Manual Enrichment */}
        <Card className="bg-[#0F141A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Search className="w-5 h-5 text-[#1EA0E6]" />
              Manuelle Anreicherung
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <Input
                placeholder="Marke (z.B. BMW)"
                value={enrichBrand}
                onChange={(e) => setEnrichBrand(e.target.value)}
                className="bg-white/5 border-white/10 text-white"
              />
              <Input
                placeholder="Modell (optional)"
                value={enrichModel}
                onChange={(e) => setEnrichModel(e.target.value)}
                className="bg-white/5 border-white/10 text-white"
              />
              <Button
                onClick={handleManualEnrich}
                disabled={enriching || !enrichBrand.trim()}
                className="bg-[#1EA0E6] hover:bg-[#1890D0] whitespace-nowrap"
              >
                {enriching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                Anreichern
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Enrichment Queue */}
          <Card className="bg-[#0F141A] border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-400" />
                  Prioritäts-Warteschlange
                </span>
                <span className="text-sm text-gray-400 font-normal">
                  Top {enrichmentQueue.length}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {enrichmentQueue.length > 0 ? (
                  enrichmentQueue.map((item, i) => (
                    <div 
                      key={i}
                      className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                    >
                      <div>
                        <p className="text-white font-medium">{item.brand} {item.model || ''}</p>
                        <p className="text-xs text-gray-400">
                          {item.search_count} Suchen • Score: {item.priority_score?.toFixed(2) || 'N/A'}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleEnrichFromQueue(item)}
                        disabled={enriching}
                        className="bg-[#1EA0E6]/20 text-[#1EA0E6] hover:bg-[#1EA0E6]/30"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4">Keine priorisierten Einträge</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Recent Jobs */}
          <Card className="bg-[#0F141A] border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Clock className="w-5 h-5 text-purple-400" />
                Letzte Anreicherungen
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {jobs.length > 0 ? (
                  jobs.map((job, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedJob(job)}
                      className="w-full flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 text-left transition-colors"
                      data-testid={`enrichment-job-${i}`}
                    >
                      <div className="flex items-center gap-3">
                        {job.status === 'completed' ? (
                          <CheckCircle className="w-5 h-5 text-green-400" />
                        ) : job.status === 'failed' ? (
                          <AlertCircle className="w-5 h-5 text-red-400" />
                        ) : (
                          <Loader2 className="w-5 h-5 text-yellow-400 animate-spin" />
                        )}
                        <div>
                          <p className="text-white text-sm">{job.brand} {job.model || ''}</p>
                          <p className="text-xs text-gray-400">
                            {job.source} · {job.created_at ? new Date(job.created_at).toLocaleString('de-DE') : ''}
                          </p>
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${
                        job.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                        job.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                        'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {job.status}
                      </span>
                    </button>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4">Keine Jobs vorhanden</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Settings */}
        <Card className="bg-[#0F141A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Settings className="w-5 h-5 text-gray-400" />
              Pipeline-Einstellungen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <label className="text-sm text-gray-400 block mb-2">Tageslimit</label>
                <Input
                  type="number"
                  value={settings.daily_limit}
                  onChange={(e) => setSettings({...settings, daily_limit: parseInt(e.target.value) || 50})}
                  className="bg-white/5 border-white/10 text-white"
                />
                <p className="text-xs text-gray-500 mt-1">Max. Anreicherungen pro Tag</p>
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-2">Cooldown (Stunden)</label>
                <Input
                  type="number"
                  value={settings.cooldown_hours}
                  onChange={(e) => setSettings({...settings, cooldown_hours: parseInt(e.target.value) || 24})}
                  className="bg-white/5 border-white/10 text-white"
                />
                <p className="text-xs text-gray-500 mt-1">Zeit zwischen Anreicherungen</p>
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-2">Prioritäts-Schwelle</label>
                <Input
                  type="number"
                  value={settings.priority_threshold}
                  onChange={(e) => setSettings({...settings, priority_threshold: parseInt(e.target.value) || 3})}
                  className="bg-white/5 border-white/10 text-white"
                />
                <p className="text-xs text-gray-500 mt-1">Min. Suchen für Auto-Enrichment</p>
              </div>
            </div>
            <div className="flex gap-4 mt-6">
              <Button onClick={handleSaveSettings} className="bg-[#1EA0E6] hover:bg-[#1890D0]">
                Speichern
              </Button>
              <Button onClick={handleResetCounter} variant="outline" className="border-white/20 text-white hover:bg-white/10">
                Tageszähler zurücksetzen
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Recommendations */}
        {qualityReport?.recommendations?.length > 0 && (
          <Card className="bg-gradient-to-r from-[#1EA0E6]/10 to-[#22C55E]/10 border-[#1EA0E6]/30">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-[#1EA0E6]" />
                Empfehlungen
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {qualityReport.recommendations.map((rec, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-300">
                    <ChevronRight className="w-4 h-4 text-[#1EA0E6] mt-1 flex-shrink-0" />
                    {rec}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Job Detail Modal */}
      {selectedJob && (
        <JobDetailModal
          job={selectedJob}
          detail={jobDetail}
          onClose={() => setSelectedJob(null)}
          apiBase={API}
        />
      )}
    </div>
  );
};

const JobDetailModal = ({ job, detail, onClose, apiBase }) => {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="min-h-screen p-4 flex items-start justify-center">
        <div className="w-full max-w-4xl bg-[#0F141A] rounded-2xl border border-white/10 my-4">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10 sticky top-0 bg-[#0F141A] z-10 rounded-t-2xl">
            <div>
              <h2 className="text-white text-lg font-bold flex items-center gap-2">
                {job.status === 'completed' ? (
                  <CheckCircle className="w-5 h-5 text-green-400" />
                ) : job.status === 'failed' ? (
                  <AlertCircle className="w-5 h-5 text-red-400" />
                ) : (
                  <Loader2 className="w-5 h-5 text-yellow-400" />
                )}
                Anreicherung: {job.brand} {job.model || ''}
              </h2>
              <p className="text-xs text-gray-400 mt-1">
                {job.source} · {job.created_at && new Date(job.created_at).toLocaleString('de-DE')}
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="p-5 space-y-4">
            {!detail ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
              </div>
            ) : detail.error ? (
              <p className="text-red-400">{detail.error}</p>
            ) : (
              <>
                {/* Stats Grid */}
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 text-center">
                  {[
                    { label: 'Modelle (Katalog)', value: detail.stats?.model_count_in_catalog, color: 'cyan' },
                    { label: 'Quellen', value: detail.stats?.source_count_in_catalog, color: 'purple' },
                    { label: 'Fahrzeuge', value: detail.stats?.vehicle_count, color: 'amber' },
                    { label: 'Mit Bild', value: detail.stats?.has_image, color: 'emerald' },
                    { label: 'Mit Specs', value: detail.stats?.has_specs, color: 'pink' },
                  ].map((s) => (
                    <div key={s.label} className={`bg-${s.color}-500/10 border border-${s.color}-500/30 rounded-lg p-2`}>
                      <p className={`text-${s.color}-300 text-xl font-bold`}>{s.value ?? 0}</p>
                      <p className="text-[10px] text-gray-400">{s.label}</p>
                    </div>
                  ))}
                </div>

                {/* Tavily summary */}
                {detail.catalog?.tavily_answer && (
                  <div>
                    <h3 className="text-cyan-300 text-xs uppercase font-semibold mb-2">KI-Zusammenfassung</h3>
                    <p className="text-gray-300 text-sm bg-cyan-500/5 border border-cyan-500/20 rounded-lg p-3">
                      {detail.catalog.tavily_answer}
                    </p>
                  </div>
                )}

                {/* Vehicles preview */}
                {detail.vehicles?.length > 0 && (
                  <div>
                    <h3 className="text-cyan-300 text-xs uppercase font-semibold mb-2">
                      Gefundene Fahrzeuge ({detail.vehicles.length})
                    </h3>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {detail.vehicles.map((v) => (
                        <div key={v.id} className="flex items-center gap-3 p-2 rounded bg-white/5">
                          {v.image?.path ? (
                            <img
                              src={`${apiBase}/api${v.image.path}`}
                              alt=""
                              className="w-12 h-12 rounded object-cover"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <div className="w-12 h-12 rounded bg-white/5 flex items-center justify-center text-[10px] text-gray-500">
                              Kein Bild
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm truncate">{v.brand} {v.model} {v.variant || ''}</p>
                            <div className="flex gap-1 mt-1">
                              <Badge ok={!!v.image?.path} label="Bild" />
                              <Badge ok={!!v.specifications && Object.keys(v.specifications).length > 0} label="Specs" />
                              <Badge ok={!!v.tuning} label="Tuning" />
                            </div>
                          </div>
                          <a
                            href={`/admin/vehicle-editor`}
                            className="text-xs text-cyan-400 hover:text-cyan-300 px-2 py-1 rounded border border-cyan-500/30"
                          >
                            Im Editor öffnen →
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Catalog models */}
                {detail.catalog?.models?.length > 0 && (
                  <div>
                    <h3 className="text-cyan-300 text-xs uppercase font-semibold mb-2">
                      Modelle im Katalog ({detail.catalog.models.length})
                    </h3>
                    <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                      {detail.catalog.models.slice(0, 30).map((m, i) => (
                        <span key={i} className="text-xs px-2 py-1 bg-white/5 rounded text-gray-300">
                          {m.name}{m.year_from ? ` (${m.year_from})` : ''}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sources */}
                {detail.catalog?.sources?.length > 0 && (
                  <div>
                    <h3 className="text-cyan-300 text-xs uppercase font-semibold mb-2">
                      Quellen ({detail.catalog.sources.length})
                    </h3>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {detail.catalog.sources.slice(0, 15).map((s, i) => (
                        <a
                          key={i}
                          href={s.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block text-xs text-cyan-400 hover:text-cyan-300 truncate"
                        >
                          ↗ {s.title || s.url}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Badge = ({ ok, label }) => (
  <span className={`text-[9px] px-1.5 py-0.5 rounded ${ok ? 'bg-green-500/20 text-green-300' : 'bg-red-500/10 text-red-400/60'}`}>
    {ok ? '✓' : '✗'} {label}
  </span>
);

export default AdminDataPipeline;
